/**
 * Parse one `gen_ed_requirements` entry into either a plain area or an
 * exclusive OR-group. ONE implementation for every card (PR #309 review:
 * UltraCompactCard and SuggestionCard carried verbatim copies, which is why
 * the W48 digit-aware separator had to be patched twice).
 *
 * Grammar (mirrors backend/services/optimizer/graph_builder._parse_gen_ed_areas):
 *  - "DSHUorDSSP", "4or6", "2G4Xor2G6X"  -> OR group (separator: 'or' between two
 *    code tokens; tokens may start with or contain digits since W48)
 *  - "DSNL(if taken with BSCI161)orDSNS" -> OR group over the two areas (UMD's
 *    conditional-lab construct; letters-only areas by construction)
 *  - anything else                        -> plain area
 */
export interface ParsedGenEdEntry {
  type: 'or' | 'plain';
  area?: string;
  areas?: string[];
}

const CONDITIONAL_RE = /^([A-Z]+)\(if taken with ([A-Z0-9]+)\)or([A-Z]+)$/;
// Lookbehind on the preceding CHARACTER, not the string start: a 3-way group
// like Merced's "3Bor4or6" must split at both separators (the start-anchored
// form only split the first). Mirrors the backend's `or(?=[A-Z0-9])`.
const OR_SPLIT_RE = /(?<=[A-Z0-9])or(?=[A-Z0-9])/;

export const parseGenEdEntry = (entry: unknown): ParsedGenEdEntry => {
  if (typeof entry !== 'string') return { type: 'plain', area: String(entry) };
  const condMatch = entry.match(CONDITIONAL_RE);
  if (condMatch) {
    return { type: 'or', areas: [condMatch[1], condMatch[3]] };
  }
  const parts = entry.split(OR_SPLIT_RE);
  if (parts.length > 1) {
    return { type: 'or', areas: parts };
  }
  return { type: 'plain', area: entry };
};

/**
 * Render an AND'd list of gen-ed entries for display: plain codes as-is, each
 * OR-group as "A or B" (the AP awards list and the AP choice dialog; the
 * backend's `gen_ed_codes` uses the same grammar as `gen_ed_requirements`).
 */
export const formatGenEdCodes = (codes: readonly string[] | undefined): string => {
  if (!codes || codes.length === 0) return '';
  return codes
    .map((entry) => {
      const parsed = parseGenEdEntry(entry);
      return parsed.type === 'or' ? (parsed.areas ?? []).join(' or ') : (parsed.area ?? '');
    })
    .join(', ');
};
